import type { ReactNode } from "react";
import InstagramIcon from "@/components/icons/ShareSocialIcon/instagramIcon";

export const SHARE_PLATFORMS = {
  COPY_LINK: "copy-link",
  TIKTOK: "tiktok",
  FACEBOOK: "facebook",
  X: "x",
  WHATSAPP: "whatsapp",
  THREADS: "threads",
  LINKEDIN: "linkedin",
  INSTAGRAM: "instagram",
} as const;

export type SharePlatformId = (typeof SHARE_PLATFORMS)[keyof typeof SHARE_PLATFORMS];

export type SharePlatformOption = {
  key: string;
  label: string;
  icon: ReactNode;
  iconWrapperClassName: string;
  onClick: () => void;
};

type SharePlatformLabels = Partial<Record<SharePlatformId, string>>;

type CreateSharePlatformOptionsConfig = {
  labels?: SharePlatformLabels;
  shareUrl: string;
  shareText: string;
  xShareText?: string;
  shareTextWithUrl: string;
  copyText?: string;
  whatsappShareBaseUrl?: string;
  onCopied?: () => void;
  onNativeShare?: (platform: SharePlatformId) => void;
  onTrack?: (platform: SharePlatformId) => void;
  onCopyLink?: () => void;
  onPlatformShare?: (platform: Exclude<SharePlatformId, "copy-link">) => void;
  onClose?: () => void;
};

type SharePlatformDefinition = {
  defaultLabel: string;
  icon: ReactNode;
  iconWrapperClassName: string;
  createOnClick: (config: CreateSharePlatformOptionsConfig) => () => void | Promise<void>;
};

const DEFAULT_SHARE_PLATFORM_LABELS: Record<SharePlatformId, string> = {
  "copy-link": "Copy link",
  tiktok: "TikTok",
  facebook: "Facebook",
  x: "X",
  whatsapp: "WhatsApp",
  threads: "Threads",
  linkedin: "LinkedIn",
  instagram: "Instagram",
};

const SHARE_PLATFORM_REGISTRY: Record<SharePlatformId, SharePlatformDefinition> = {
  "copy-link": {
    defaultLabel: DEFAULT_SHARE_PLATFORM_LABELS["copy-link"],
    icon: (
      // eslint-disable-next-line @next/next/no-img-element
      <img src="/shares/copy-link.png" alt="" className="h-6 w-6 object-contain" />
    ),
    iconWrapperClassName: "rounded-[6px] bg-white",
    createOnClick:
      ({
        copyText,
        onCopied,
        onCopyLink,
        onTrack,
        onClose,
      }) =>
      async () => {
        if (!copyText) return;
        try {
          await navigator.clipboard.writeText(copyText);
        } catch {
          await fallbackCopy(copyText);
        }
        onCopied?.();
        onCopyLink?.();
        onTrack?.(SHARE_PLATFORMS.COPY_LINK);
        onClose?.();
      },
  },
  tiktok: {
    defaultLabel: DEFAULT_SHARE_PLATFORM_LABELS.tiktok,
    icon: (
      // eslint-disable-next-line @next/next/no-img-element
      <img src="/shares/tiktok.png" alt="" className="h-6 w-6 object-contain" />
    ),
    iconWrapperClassName: "rounded-[5px] bg-black",
    createOnClick:
      ({ shareUrl, shareText, onPlatformShare, onTrack, onClose }) =>
      () => {
        if (!shareUrl) return;
        onPlatformShare?.(SHARE_PLATFORMS.TIKTOK);
        window.open(buildShareLink("https://www.tiktok.com/share", { url: shareUrl, text: shareText }), "_blank");
        onTrack?.(SHARE_PLATFORMS.TIKTOK);
        onClose?.();
      },
  },
  facebook: {
    defaultLabel: DEFAULT_SHARE_PLATFORM_LABELS.facebook,
    icon: (
      // eslint-disable-next-line @next/next/no-img-element
      <img src="/shares/facebook.png" alt="" className="h-6 w-6 object-contain" />
    ),
    iconWrapperClassName: "rounded-[5px] bg-[#0866FF]",
    createOnClick:
      ({ shareUrl, shareText, onPlatformShare, onTrack, onClose }) =>
      () => {
        if (!shareUrl) return;
        onPlatformShare?.(SHARE_PLATFORMS.FACEBOOK);
        window.open(buildShareLink("https://www.facebook.com/sharer/sharer.php", { u: shareUrl, quote: shareText }), "_blank");
        onTrack?.(SHARE_PLATFORMS.FACEBOOK);
        onClose?.();
      },
  },
  x: {
    defaultLabel: DEFAULT_SHARE_PLATFORM_LABELS.x,
    icon: (
      // eslint-disable-next-line @next/next/no-img-element
      <img src="/shares/x.png" alt="" className="h-6 w-6 object-contain" />
    ),
    iconWrapperClassName: "rounded-[5px] bg-black",
    createOnClick:
      ({ shareUrl, shareText, xShareText, onPlatformShare, onTrack, onClose }) =>
      () => {
        if (!shareUrl) return;
        onPlatformShare?.(SHARE_PLATFORMS.X);
        window.open(
          buildShareLink("https://twitter.com/intent/tweet", {
            text: xShareText || shareText,
            url: shareUrl,
          }),
          "_blank"
        );
        onTrack?.(SHARE_PLATFORMS.X);
        onClose?.();
      },
  },
  whatsapp: {
    defaultLabel: DEFAULT_SHARE_PLATFORM_LABELS.whatsapp,
    icon: (
      // eslint-disable-next-line @next/next/no-img-element
      <img src="/shares/whatsapp.png" alt="" className="h-6 w-6 object-contain" />
    ),
    iconWrapperClassName: "rounded-[5px] bg-[#25D366]",
    createOnClick:
      ({
        shareTextWithUrl,
        shareUrl,
        whatsappShareBaseUrl = "https://wa.me/",
        onPlatformShare,
        onTrack,
        onClose,
      }) =>
      () => {
        const text = shareTextWithUrl || shareUrl;
        if (!text) return;
        onPlatformShare?.(SHARE_PLATFORMS.WHATSAPP);
        window.open(buildShareLink(whatsappShareBaseUrl, { text }), "_blank");
        onTrack?.(SHARE_PLATFORMS.WHATSAPP);
        onClose?.();
      },
  },
  threads: {
    defaultLabel: DEFAULT_SHARE_PLATFORM_LABELS.threads,
    icon: (
      // eslint-disable-next-line @next/next/no-img-element
      <img src="/shares/threads.png" alt="" className="h-6 w-6 object-contain" />
    ),
    iconWrapperClassName: "rounded-[5px] bg-black",
    createOnClick:
      ({ shareTextWithUrl, shareUrl, onPlatformShare, onTrack, onClose }) =>
      () => {
        const text = shareTextWithUrl || shareUrl;
        if (!text) return;
        onPlatformShare?.(SHARE_PLATFORMS.THREADS);
        window.open(buildShareLink("https://www.threads.net/intent/post", { text }), "_blank");
        onTrack?.(SHARE_PLATFORMS.THREADS);
        onClose?.();
      },
  },
  linkedin: {
    defaultLabel: DEFAULT_SHARE_PLATFORM_LABELS.linkedin,
    icon: (
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M17.2222 0H2.77778C1.2435 0 0 1.2435 0 2.77778V17.2222C0 18.7565 1.2435 20 2.77778 20H17.2222C18.7565 20 20 18.7565 20 17.2222V2.77778C20 1.2435 18.7565 0 17.2222 0ZM6.11111 16.6667H3.33333V7.77778H6.11111V16.6667ZM4.72222 6.55556C3.83244 6.55556 3.11111 5.83422 3.11111 4.94444C3.11111 4.05467 3.83244 3.33333 4.72222 3.33333C5.612 3.33333 6.33333 4.05467 6.33333 4.94444C6.33333 5.83422 5.612 6.55556 4.72222 6.55556ZM16.6667 16.6667H13.8889V12.3333C13.8889 11.3004 13.8693 9.972 12.4502 9.972C10.9996 9.972 10.7778 11.1049 10.7778 12.2582V16.6667H8V7.77778H10.6667V8.98889H10.7049C11.076 8.28578 11.9844 7.54444 13.3333 7.54444C16.1431 7.54444 16.6667 9.39378 16.6667 11.7924V16.6667Z" fill="white" />
      </svg>
    ),
    iconWrapperClassName: "rounded-[5px] bg-[#0077B5]",
    createOnClick:
      ({ shareTextWithUrl, shareUrl, onPlatformShare, onTrack, onClose }) =>
      () => {
        onPlatformShare?.(SHARE_PLATFORMS.LINKEDIN);
        window.open(
          `https://www.linkedin.com/feed/?shareActive=true&text=${encodeURIComponent(shareTextWithUrl)}&url=${encodeURIComponent(shareUrl)}&shareUrl=${encodeURIComponent(shareUrl)}`,
          "_blank"
        );
        onTrack?.(SHARE_PLATFORMS.LINKEDIN);
        onClose?.();
      },
  },
  instagram: {
    defaultLabel: DEFAULT_SHARE_PLATFORM_LABELS.instagram,
    icon: <InstagramIcon className="h-full w-full" />,
    iconWrapperClassName: "overflow-hidden bg-white",
    createOnClick:
      ({ onNativeShare, onPlatformShare, onTrack, onClose }) =>
      () => {
        onPlatformShare?.(SHARE_PLATFORMS.INSTAGRAM);
        onTrack?.(SHARE_PLATFORMS.INSTAGRAM);
        if (onNativeShare) {
          onNativeShare(SHARE_PLATFORMS.INSTAGRAM);
          onClose?.();
        }
      },
  },
};

export const buildShareLink = (base: string, params: Record<string, string>) => {
  const search = new URLSearchParams(params);
  return `${base}?${search.toString()}`;
};

export const fallbackCopy = async (text: string) => {
  const textarea = document.createElement("textarea");
  textarea.value = text;
  textarea.style.position = "fixed";
  textarea.style.left = "-9999px";
  textarea.style.top = "0";
  document.body.appendChild(textarea);
  textarea.focus();
  textarea.select();
  document.execCommand("copy");
  document.body.removeChild(textarea);
};

export const createSharePlatformOptions = ({
  labels,
  shareUrl,
  shareText,
  xShareText,
  shareTextWithUrl,
  copyText = shareUrl,
  whatsappShareBaseUrl = "https://wa.me/",
  onCopied,
  onNativeShare,
  onTrack,
  onCopyLink,
  onPlatformShare,
  onClose,
}: CreateSharePlatformOptionsConfig): SharePlatformOption[] => {
  const resolvedLabels = {
    ...DEFAULT_SHARE_PLATFORM_LABELS,
    ...labels,
  };

  return Object.entries(SHARE_PLATFORM_REGISTRY).map(([platformId, definition]) => ({
    key: platformId,
    label: resolvedLabels[platformId as SharePlatformId] ?? definition.defaultLabel,
    icon: definition.icon,
    iconWrapperClassName: definition.iconWrapperClassName,
    onClick: definition.createOnClick({
      labels,
      shareUrl,
      shareText,
      xShareText,
      shareTextWithUrl,
      copyText,
      whatsappShareBaseUrl,
      onCopied,
      onNativeShare,
      onTrack,
      onCopyLink,
      onPlatformShare,
      onClose,
    }),
  }));
};
